import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { agentAPI } from '@/api/agent';
import { showError } from '@/utils/errorHandler';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft,
  RefreshCw,
  Server,
  Cpu,
  HardDrive,
  Activity,
  Clock,
  Box,
  FileText,
  X,
} from 'lucide-react';

// Allowed email for this page (hardcoded for security)
const ALLOWED_EMAIL = 'o.antsiferov@logitex.kz';

const AgentDashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [status, setStatus] = useState(null);
  const [systemInfo, setSystemInfo] = useState(null);
  const [containers, setContainers] = useState([]);
  const [selectedContainer, setSelectedContainer] = useState(null);
  const [containerLogs, setContainerLogs] = useState('');
  const [logsLoading, setLogsLoading] = useState(false);

  // Check access - only allowed email
  const hasAccess = user?.email === ALLOWED_EMAIL;

  const loadData = useCallback(async () => {
    if (!hasAccess) return;

    try {
      const [statusData, systemData, containersData] = await Promise.all([
        agentAPI.getStatus(),
        agentAPI.getSystemInfo(),
        agentAPI.getContainers(),
      ]);

      setStatus(statusData);
      setSystemInfo(systemData);
      setContainers(containersData.containers || []);
    } catch (error) {
      if (error.response?.status === 403) {
        showError(error, 'Agent Bridge отключен или доступ запрещен');
      } else {
        showError(error, 'Ошибка загрузки данных Agent Server');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [hasAccess]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
  };

  const handleViewLogs = async (containerName) => {
    setSelectedContainer(containerName);
    setLogsLoading(true);
    setContainerLogs('');

    try {
      const data = await agentAPI.getContainerLogs(containerName, 200);
      setContainerLogs(data.logs || 'No logs available');
    } catch (error) {
      if (error.response?.status === 404) {
        setContainerLogs('Container not found or not in allowlist');
      } else {
        showError(error, 'Ошибка загрузки логов');
        setContainerLogs('Error loading logs');
      }
    } finally {
      setLogsLoading(false);
    }
  };

  const closeLogs = () => {
    setSelectedContainer(null);
    setContainerLogs('');
  };

  // Access denied
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <Server className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Доступ запрещен
            </h2>
            <p className="text-gray-600 mb-4">
              Эта страница доступна только для администратора Agent Server.
            </p>
            <Button onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Вернуться на главную
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl">Загрузка Agent Server...</div>
      </div>
    );
  }

  const isConnected = status?.bridge?.healthy;
  const bridgeVersion = status?.bridge?.version;
  const latencyMs = status?.connection?.latency_ms;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Agent Server Dashboard
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Procurement Intelligence System
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant={isConnected ? 'default' : 'destructive'}>
                {isConnected ? 'Connected' : 'Disconnected'}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <RefreshCw
                  className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`}
                />
                Обновить
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Connection Status */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Server className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Bridge Version</p>
                  <p className="font-semibold">{bridgeVersion || 'N/A'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Activity className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Latency</p>
                  <p className="font-semibold">
                    {latencyMs ? `${latencyMs.toFixed(1)} ms` : 'N/A'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Clock className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Uptime</p>
                  <p className="font-semibold">
                    {systemInfo?.uptime || 'N/A'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Box className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Containers</p>
                  <p className="font-semibold">{containers.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* System Metrics */}
        {systemInfo && !systemInfo.error && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Cpu className="w-5 h-5" />
                System Metrics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* CPU */}
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-gray-600">CPU</span>
                    <span className="text-sm font-medium">
                      {systemInfo.cpu?.percent?.toFixed(1) || 0}%
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full">
                    <div
                      className="h-2 bg-blue-500 rounded-full"
                      style={{
                        width: `${Math.min(systemInfo.cpu?.percent || 0, 100)}%`,
                      }}
                    />
                  </div>
                </div>

                {/* Memory */}
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-gray-600">Memory</span>
                    <span className="text-sm font-medium">
                      {systemInfo.memory?.percent?.toFixed(1) || 0}%
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full">
                    <div
                      className="h-2 bg-green-500 rounded-full"
                      style={{
                        width: `${Math.min(systemInfo.memory?.percent || 0, 100)}%`,
                      }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {systemInfo.memory?.used_gb?.toFixed(1) || 0} /{' '}
                    {systemInfo.memory?.total_gb?.toFixed(1) || 0} GB
                  </p>
                </div>

                {/* Disk */}
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-gray-600">Disk</span>
                    <span className="text-sm font-medium">
                      {systemInfo.disk?.percent?.toFixed(1) || 0}%
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full">
                    <div
                      className="h-2 bg-purple-500 rounded-full"
                      style={{
                        width: `${Math.min(systemInfo.disk?.percent || 0, 100)}%`,
                      }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {systemInfo.disk?.used_gb?.toFixed(1) || 0} /{' '}
                    {systemInfo.disk?.total_gb?.toFixed(1) || 0} GB
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Containers */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Box className="w-5 h-5" />
              Docker Containers
            </CardTitle>
          </CardHeader>
          <CardContent>
            {containers.length === 0 ? (
              <p className="text-gray-500 text-center py-4">
                No containers found
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {containers.map((container) => (
                  <div
                    key={container.name}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-2 h-2 rounded-full ${
                          container.status === 'running'
                            ? 'bg-green-500'
                            : 'bg-gray-400'
                        }`}
                      />
                      <div>
                        <p className="font-medium text-sm">{container.name}</p>
                        <p className="text-xs text-gray-500">
                          {container.status}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewLogs(container.name)}
                    >
                      <FileText className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Logs Modal */}
        {selectedContainer && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] m-4 flex flex-col">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="font-semibold">
                  Logs: {selectedContainer}
                </h3>
                <Button variant="ghost" size="sm" onClick={closeLogs}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex-1 overflow-auto p-4 bg-gray-900">
                {logsLoading ? (
                  <p className="text-gray-400">Loading logs...</p>
                ) : (
                  <pre className="text-sm text-gray-100 whitespace-pre-wrap font-mono">
                    {containerLogs}
                  </pre>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AgentDashboardPage;
